import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { COMMAND_LABELS, type ChargerCommand } from '@pulsepoint/shared';
import { useCommandHistory, type Charger, type CommandHistoryRow } from '../../api/chargers';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { StatusLabel, type StatusTone } from '../../components/StatusLabel';
import { formatDateTime } from '../../lib/format';
import { describeResult } from './useCommand';

const TONE: Record<CommandHistoryRow['status'], StatusTone> = { accepted: 'success', rejected: 'fault', error: 'fault', timeout: 'fault', pending: 'neutral', sent: 'neutral' };
const WORD: Record<CommandHistoryRow['status'], string> = { accepted: 'Accepted', rejected: 'Refused', error: 'Error', timeout: 'No answer', pending: 'Waiting', sent: 'Sent' };

function details(r: CommandHistoryRow): string {
  const p = r.payload as Record<string, unknown>;
  switch (r.action) {
    case 'Reset': return `${p.type} restart`;
    case 'RemoteStartTransaction': return `Connector ${p.connectorId}`;
    case 'RemoteStopTransaction': return `Session ${p.transactionId}`;
    case 'UnlockConnector': return `Connector ${p.connectorId}`;
    case 'TriggerMessage': return String(p.requestedMessage);
    case 'ChangeConfiguration': return `${p.key} = ${p.value}`;
    case 'ChangeAvailability': return `Connector ${p.connectorId}: ${p.type}`;
    default: return '';
  }
}

/** The last 50 commands sent to this charger, who sent them, and the answers. */
export function HistoryTab({ charger }: { charger: Charger }) {
  const history = useCommandHistory(charger.id);
  return (
    <DataTable
      label="Commands"
      rows={history.data}
      loading={history.isPending}
      error={history.error}
      columns={[
        { key: 'when', header: 'When', render: (r) => <span className="num">{formatDateTime(r.createdAt)}</span> },
        {
          key: 'what',
          header: 'Command',
          render: (r) => (
            <Box>
              <Typography>{COMMAND_LABELS[r.action as ChargerCommand] ?? r.action}</Typography>
              <Typography variant="body2" color="textSecondary" className="num">{details(r)}</Typography>
            </Box>
          ),
        },
        { key: 'who', header: 'By', hideBelow: 'sm', render: (r) => r.requestedBy ?? <Typography color="textSecondary">Pulsepoint</Typography> },
        { key: 'result', header: 'Result', render: (r) => <span title={describeResult(r).text}><StatusLabel tone={TONE[r.status]}>{WORD[r.status]}</StatusLabel></span> },
      ]}
      rowKey={(r) => r.id}
      pageSize={50}
      empty={<EmptyState title="No commands yet">Restarts, remote starts and other commands to this charger appear here.</EmptyState>}
    />
  );
}
